import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Card, Accordion, Form } from 'react-bootstrap';

import * as Helper from '../Helpers/PresetHelper';

import CardHeaderToggle from '@components/Common/Accordion/CardHeaderToggle';
import { WeatherStationDropdown } from '@components/Common/WeatherStationDropdown';

import * as PresetButtons from '../Helpers/GraphPresetButtons';
import * as Actions from '../Helpers/Actions';
import { ObservationDataItem } from './ObservationDataItem';

/**
 * A component representing a group of weather stations and their related data.
 *
 * @component
 * @param {number} weatherStationId - The ID of the current weather station group.
 * @param {number} index - The index of the weather station group.
 * @param {Object} stationData - The data associated with the current weather station group.
 * @param {Object} data - Data containing a list of data graphs.
 * @param {Function} toggle - A function to toggle the weather station group.
 * @param {string|null} activeKey - The active key of the weather station group.
 * @param {Function} setActiveKey - A function to set the active key of the weather station group.
 * @param {Function} ref - A reference to the component.
 * @returns {React.Component} The rendered WeatherStationGroup component.
 */
export const WeatherStationGroup = React.forwardRef((props, ref) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [observationData, setObservationData] = useState([]);
  const [disabled, setDisabled] = useState(false);

  // Create a unique section ID for this weather station group
  const sectionId = `Station_${props.weatherStationId || props.index}`;

  // Get the display order of the station data
  const displayOrder = props.stationData.DisplayOrder;

  // Retrieve the weather station information for the first persistent graph filter
  const weatherStation = props.stationData.PersistentGraphFilters[props.weatherStationId][0].weatherStationInfo;

  // Determine the station name, default to 'Defer/Any' if no weather station information is available
  const stationName = weatherStation != null ? weatherStation.DropdownText : 'Any';

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);

      try {
        let data = [];
        if (props.weatherStationId !== -1 && weatherStation) {
          // Fetch weather station data if a specific weather station is selected
          data = await Helper.getWeatherStationData(weatherStation.Id);
        } else {
          // Fetch general observation data if 'Defer/Any' station is selected
          data = await Helper.getObservationData();
        }

        // Check if the component is still mounted before updating state
        if (isMounted) {
          setLoading(false);
          setObservationData(data);
        }
      } catch (error) {
        console.error('Unable to fetchData for Observation Type dropdown');
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Card key={sectionId} border="light" className="page-card">
      <CardHeaderToggle
        eventKey={sectionId}
        as="h5"
        title={stationName}
        onClick={() => props.toggle(sectionId)}
        isLink={false}
        isLeft={true}
        extraButtons={
          props.readOnly
            ? null
            : PresetButtons.stationButtons(
                displayOrder,
                props.data,
                props.weatherStationId,
                props.setActiveKey,
                props.formik,
              )
        }
      />
      <Accordion.Collapse eventKey={sectionId}>
        <Card.Body>
          <Row>
            <Col sm={8} md={10} lg={12}>
              {/* TODO: has to be label only, no drop down or filter when Read Only*/}
              <WeatherStationDropdown
                hasDeferred={true}
                shouldDispatch={false}
                postChange={(option) => {
                  // Update the selected weather station ID when the dropdown value changes
                  dispatch(
                    Actions.updateGraphFilterWeatherStation(
                      props.data.DataGraphPresetId,
                      displayOrder,
                      props.weatherStationId,
                      option.value,
                      props.formik,
                    ),
                  );

                  props.toggle(`Station_${option.value || props.index}`);
                }}
                weatherStation={weatherStation}
                isDisabled={disabled || props.readOnly}
              />
            </Col>
          </Row>
          <ObservationDataItem
            ref={ref}
            data={props.data}
            stationData={props.stationData}
            observationData={observationData}
            displayOrder={displayOrder}
            isLoading={loading}
            weatherStationId={props.weatherStationId}
            setWXDropdownDisabled={setDisabled}
            formik={props.formik}
            dataGraphIndex={props.dataGraphIndex}
            readOnly={props.readOnly}
          />
        </Card.Body>
      </Accordion.Collapse>
    </Card>
  );
});

// Display name for the component
WeatherStationGroup.displayName = 'WeatherStationGroup';
